import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
    Badge, IconButton, Menu, MenuItem, ListItemText, Tooltip,
    Divider, Box, Button, Typography, Stack, Fade, ListItemIcon
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import useNotifications from '../../../store/notifications';


const srOnly = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
};

const formatRelative = (d) => {
    try {
        const date = new Date(d);
        const diffMs = Date.now() - date.getTime();
        const mins = Math.round(diffMs / 60000);
        const hours = Math.round(mins / 60);
        const days = Math.round(hours / 24);



        if (mins < 1) return "à l’instant";
        if (mins < 60) return `il y a ${mins} min`;
        if (hours < 24) return `il y a ${hours} h`;
        if (days === 1) return "hier";
        if (days < 7) return `il y a ${days} j`;

        return date.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};



export default function NotificationMenu() {
    const [anchorEl, setAnchorEl] = useState(null);
    const items = useNotifications((s) => s.items);
    const markRead = useNotifications((s) => s.markRead);
    const markAllRead = useNotifications((s) => s.markAllRead);

    const unread = useMemo(() => items.filter((n) => !n.readAt).length, [items]);
    const open = Boolean(anchorEl);

    const markingRef = useRef(false);
    const prevUnreadRef = useRef(unread);
    const total = items.length;



    const handleOpen = useCallback((e) => {
        setAnchorEl(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => setAnchorEl(null), []);

    const handleMarkAll = useCallback(async () => {
        if (unread === 0) return;
        try {
            await axios.post('/api/comptable/notifications/read-all');
        } catch (e) {
        }
        markAllRead();
    }, [unread, markAllRead]);

    const handleItemClick = useCallback(async (n) => {
        if (!n.readAt && n._id) {
            try {
                await axios.post(`/api/comptable/notifications/${n._id}/read`);
            } catch (e) {
            }
            markRead(n._id);
        }
        // TODO: navigation si nécessaire (e.g., useNavigate vers un lien lié à la notif)
    }, [markRead]);

    // Auto “read-all” à l’ouverture avec feedback
    useEffect(() => {
        if (!open) {
            markingRef.current = false;
            return;
        }
        if (unread === 0 || markingRef.current) return;

        markingRef.current = true;
        (async () => {
            try {
                await axios.post('/api/comptable/notifications/read-all');
            } catch (e) {
            }
            markAllRead();
        })();
    }, [open, unread, markAllRead]);

    // Annonce SR quand unread change
    useEffect(() => {
        if (prevUnreadRef.current !== unread) {
            prevUnreadRef.current = unread;
        }
    }, [unread]);

    const getIcon = (n) => {
        // Exemple : supposez que n.type est 'info', 'success', 'error'
        switch (n.type) {
            case 'success': return <CheckCircleIcon color="success" fontSize="small" />;
            case 'error': return <ErrorIcon color="error" fontSize="small" />;
            default: return <InfoIcon color="info" fontSize="small" />;
        }
    };

    return (
        <>
            <Box component="span" sx={srOnly} aria-live="polite" role="status">
                {unread === 0
                    ? "Aucune notification non lue"
                    : `${unread} notification${unread > 1 ? 's' : ''} non lue${unread > 1 ? 's' : ''}`}
            </Box>

            <Tooltip title="Notifications" arrow>
                <IconButton
                    aria-label={`Ouvrir les notifications (${unread} non lue${unread > 1 ? 's' : ''})`}
                    onClick={handleOpen}
                    sx={{ mr: 1 }}
                    aria-haspopup="true"
                    aria-controls={open ? 'notifications-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                >
                    <Badge
                        badgeContent={unread}
                        max={99}
                        color="error"
                        invisible={unread === 0}
                        overlap="circular"
                    >                        <NotificationsIcon color="action" />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                id="notifications-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                TransitionComponent={Fade}
                slotProps={{
                    paper: {
                        elevation: 3,
                        sx: {
                            width: { xs: '90vw', sm: 380 },
                            maxHeight: 420,
                            overflowY: 'auto',
                            mt: 1.5,
                            borderRadius: 2,
                        },
                    },
                    transition: { timeout: 200 },            // (optional) transition props
                }}
                MenuListProps={{ dense: true, 'aria-label': 'Liste des notifications' }}
                disableAutoFocusItem // Permet de garder le focus sur l'item cliqué
            >
                <Box
                    sx={{
                        px: 2, py: 1.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        bgcolor: 'background.default',
                    }}
                >
                    <Typography variant="h6" fontWeight="medium">
                        Notifications{total > 0 ? ` (${total})` : ''}
                    </Typography>

                    {unread > 0 && (
                        <Button size="small" variant="text" color="primary" onClick={handleMarkAll}>
                            Tout marquer lu
                        </Button>
                    )}
                </Box>

                <Divider />

                {items.length === 0 ? (
                    <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">Aucune notification</Typography>
                    </MenuItem>
                ) : (
                    items.slice(0, 10).map((n, index) => { // Limite à 10 pour perf ; ajoutez pagination si besoin
                        const isUnread = !n.readAt;
                        const date = n.createdAt || n.timestamp;
                        return (
                            <Fade in key={n._id || n.meta?.syncId} timeout={300 + index * 50}>
                                <MenuItem
                                    onClick={() => handleItemClick(n)}
                                    sx={{
                                        py: 1.5,
                                        px: 2,
                                        transition: 'background-color 0.2s',
                                        '&:hover': { bgcolor: 'action.hover' },
                                        opacity: isUnread ? 1 : 0.7, // Feedback visuel pour lus
                                    }}
                                    divider={index < items.length - 1} // Divider subtil entre items
                                    aria-readonly={!isUnread ? 'true' : undefined}
                                >
                                    <ListItemIcon>{getIcon(n)}</ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                                <Typography
                                                    component="span"
                                                    variant="body1"
                                                    noWrap
                                                    sx={{ flex: 1, fontWeight: isUnread ? 'medium' : 'normal' }}
                                                    title={n.title || 'Synchronisation terminée'}
                                                >
                                                    {n.title || 'Synchronisation terminée'}
                                                </Typography>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ flexShrink: 0 }}
                                                    title={date ? new Date(date).toLocaleString('fr-FR') : ''}
                                                >
                                                    {formatRelative(date)}
                                                </Typography>
                                            </Stack>
                                        }
                                        secondary={
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.5,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                title={n.message}
                                            >
                                                {n.message}
                                            </Typography>
                                        }
                                    />
                                </MenuItem>
                            </Fade>
                        );
                    })
                )}

                {items.length > 10 && (
                    <Box sx={{ p: 1.5, textAlign: 'center' }}>
                        <Button size="small" variant="outlined" onClick={() => { /* TODO: navigate('/notifications') */ }}>
                            Voir plus de notifications
                        </Button>
                    </Box>
                )}
            </Menu>
        </>
    );
}